/**
 * HTTP + SSE message service for real-time chat
 */

import { v4 as uuidv4 } from 'uuid';
import { useMessageStore, Message } from '../stores/messageStore';
import { useConversationStore } from '../stores/conversationStore';
import api from './api'; // Use the shared API instance with authentication
import { logger } from '../utils/logger';
import { streamManager } from './streamManager';
import {
  ToolCallRequestEvent,
  ToolCallResponseEvent,
  ToolConfirmationRequiredEvent,
  ConversationStatusEvent,
  ToolConfirmationOutcome,
  ConversationStatus
} from '../../../shared-types/dist/index.js';


// Types for message operations
interface ImageData {
  data: string; // base64 encoded image data
  mimeType: string;
  filename?: string;
}

// Tool image extraction function removed - tools now execute in background

// AI Provider types
export type ProviderType = 'gemini' | 'claude';

interface SendMessageData {
  conversationId: string;
  content: string;
  attachments?: string[];
  images?: ImageData[];
  model?: string;
  provider?: ProviderType; // AI provider: 'gemini' | 'claude'
  placeholderMessageId?: string;
  skipUserMessage?: boolean; // Skip creating user message if already created
}

interface SendMessageResponse {
  message: {
    id: string;
    role: 'user';
    content: string;
    timestamp: string;
    attachments: string[];
    status: string;
    metadata?: any;
  };
  streamUrl: string;
}

// SSE Event types (for future use)
// interface SSEEvent {
//   type: string;
//   data: any;
// }

class MessageService {
  // Stream management is now handled by streamManager
  // Removed pendingConfirmations since they're managed by the store
  
  constructor() {
    // Set up global event listeners for stream events
    this.setupGlobalEventListeners();
  }

  // Store event handlers as instance properties to ensure stable references
  private eventHandlers: { [key: string]: (event: CustomEvent) => void } = {};
  private eventListenersSetup = false;

  /**
   * Check if event listeners are ready
   */
  public areEventListenersReady(): boolean {
    return this.eventListenersSetup;
  }

  /**
   * Set up global event listeners for stream manager events
   */
  public setupGlobalEventListeners(): void {
    // Prevent setting up listeners multiple times
    if (this.eventListenersSetup) {
      logger.debug('🚫 Event listeners already set up, skipping');
      return;
    }

    // Message start handler
    this.eventHandlers.handleMessageStart = (event: CustomEvent) => {
      try {
        const { conversationId, data } = event.detail;
        const parsedData = JSON.parse(data);
        
        logger.debug('📤 Global SSE: AI message streaming started:', parsedData);

        // Check if message already exists to prevent duplicates
        const existingMessage = useMessageStore.getState().getMessageById(parsedData.messageId);
        if (existingMessage && !existingMessage.metadata?.isPlaceholder) {
          logger.warn('⚠️ DUPLICATE: Message already exists, skipping creation:', parsedData.messageId);
          return;
        }
        
        // Check if this is the first AI message in the conversation
        const existingMessages = useMessageStore.getState().getMessagesForConversation(conversationId);
        const nonPlaceholderAIMessages = existingMessages.filter(m => m.role === 'assistant' && !m.metadata?.isPlaceholder);
        const isFirstAIMessage = nonPlaceholderAIMessages.length === 0;
        
        if (isFirstAIMessage) {
          console.log('🎯 FIRST AI MESSAGE: Creating first AI message for conversation:', conversationId);
          console.log('🎯 FIRST AI MESSAGE: Event listeners ready, thinking events will be captured');
        }

        // Create initial streaming message with simplified thinking structure
        const message: Message = {
          id: parsedData.messageId,
          conversationId: conversationId,
          role: 'assistant' as const,
          content: '',
          timestamp: new Date(parsedData.timestamp),
          attachments: [],
          metadata: {
            streamComplete: false,
          },
          status: 'streaming' as const,
          thinkingHistory: [],
          currentThinkingSubject: undefined,
          currentThinking: undefined,
        };

        // Check if there's a placeholder to replace - handle this once for all messages
        const placeholderMessageId = (window as any).__placeholderMessageId;
        if (placeholderMessageId) {
          const placeholderMsg = useMessageStore.getState().getMessageById(placeholderMessageId);
          if (placeholderMsg && placeholderMsg.metadata?.isPlaceholder) {
            console.log('🔄 Transforming placeholder message into real message');

            // Instead of removing and creating new, UPDATE the placeholder to become the real message
            // This maintains visual continuity
            const updatedMessage: Partial<Message> = {
              id: parsedData.messageId, // Update ID to server's ID
              timestamp: new Date(parsedData.timestamp),
              metadata: {
                ...placeholderMsg.metadata,
                isPlaceholder: false, // No longer a placeholder
                streamComplete: false,
              },
              // Preserve thinking history but mark all as completed
              thinkingHistory: placeholderMsg.thinkingHistory?.map(t => ({
                ...t,
                completed: true
              })) || [],
              currentThinkingSubject: undefined, // Clear current since placeholders are done
            };

            // First remove the old placeholder
            useMessageStore.getState().removeMessage(placeholderMessageId);

            // Then add the message with the new server ID but preserved history
            const transformedMessage = {
              ...placeholderMsg,
              ...updatedMessage,
              id: parsedData.messageId, // Ensure we use the server's ID
            } as Message;

            logger.debug('🆕 Adding transformed message with server ID:', parsedData.messageId);
            useMessageStore.getState().addMessage(transformedMessage);
            useMessageStore.getState().setStreamingMessage(parsedData.messageId);

            // Clear the stored placeholder ID
            delete (window as any).__placeholderMessageId;

            console.log('✅ Placeholder smoothly transformed into real message');
            return; // Exit early since we've handled the message
          }
        }

        // Only create a new message if we didn't transform a placeholder
        logger.debug('🆕 Creating new AI message:', parsedData.messageId);
        useMessageStore.getState().addMessage(message);
        useMessageStore.getState().setStreamingMessage(parsedData.messageId);
        
        if (isFirstAIMessage) {
          console.log('✅ FIRST AI MESSAGE: Message created and ready for thinking/content events');
        }

      } catch (error) {
        console.error('❌ Error handling global message_start:', error);
      }
    };

    // Message chunk handler
    this.eventHandlers.handleMessageChunk = (event: CustomEvent) => {
      try {
        const { data } = event.detail;
        const parsedData = JSON.parse(data);
        
        if (parsedData.messageId) {
          console.debug('📝 Processing chunk:', parsedData.index, parsedData.chunk?.substring(0, 50) + '...');
          
          const messageStore = useMessageStore.getState();
          const message = messageStore.getMessageById(parsedData.messageId);
          
          // Check for duplicate chunks
          if (message && typeof parsedData.index === 'number') {
            const lastChunkIndex = message.metadata?.lastChunkIndex || -1;
            if (parsedData.index <= lastChunkIndex) {
              console.warn('⚠️ Duplicate chunk detected, skipping:', parsedData.index, 'Last processed:', lastChunkIndex);
              return;
            }
            
            // Update the last processed chunk index
            messageStore.updateMessage(parsedData.messageId, {
              metadata: {
                ...message.metadata,
                lastChunkIndex: parsedData.index
              }
            });
          }
          
          // Complete current thinking when first content chunk arrives
          if (message && message.content === '' && message.currentThinkingSubject) {
            console.log('🧹 Completing current thinking as content starts arriving');
            messageStore.completeCurrentThinking(parsedData.messageId);
          }
          
          // Simply append the chunk to message content
          messageStore.appendToMessage(parsedData.messageId, parsedData.chunk);
        }

      } catch (error) {
        console.error('❌ Error handling global message_chunk:', error);
      }
    };

    // Message complete handler
    this.eventHandlers.handleMessageComplete = (event: CustomEvent) => {
      try {
        const { conversationId, data } = event.detail;
        const parsedData = JSON.parse(data);
        this.handleMessageCompletion(parsedData, conversationId);

      } catch (error) {
        console.error('❌ Error handling global message_complete:', error);
      }
    };

    // Token count update handler
    this.eventHandlers.handleTokenCountUpdate = (event: CustomEvent) => {
      try {
        const { data } = event.detail;
        const parsedData = JSON.parse(data);
        logger.debug('📊 Received token count update:', parsedData);
        console.log('📊 Token count update event received:', parsedData);
        
        // Update the message with the token count
        const messageStore = useMessageStore.getState();
        if (parsedData.messageId && parsedData.inputTokenCount !== undefined) {
          // Get the current message to preserve existing metadata
          const currentMessage = messageStore.getMessageById(parsedData.messageId);
          
          console.log('📊 Updating message with inputTokenCount:', {
            messageId: parsedData.messageId,
            inputTokenCount: parsedData.inputTokenCount,
            currentMetadata: currentMessage?.metadata
          });
          
          // Update the message with input token count
          messageStore.updateMessage(parsedData.messageId, {
            metadata: {
              ...currentMessage?.metadata,
              inputTokenCount: parsedData.inputTokenCount,
              streamComplete: currentMessage?.metadata?.streamComplete || false
            }
          });
          
          console.log('✅ Message updated with inputTokenCount');
        }
      } catch (error) {
        console.error('❌ Error handling token count update:', error);
      }
    };

    // Tool call handlers
    this.eventHandlers.handleToolRequest = (event: CustomEvent) => {
      try {
        const { data } = event.detail;
        const parsedData: ToolCallRequestEvent = JSON.parse(data);
        logger.debug('🔧 Global tool call requested:', parsedData);
        
        // Keep conversation Active during tool execution - don't pause streaming
        // This prevents UI blocking while tools execute in background
        
        // Complete any open content segments to prevent "Continuing..." UI
        const streamingMessageId = useMessageStore.getState().streamingMessageId;
        if (streamingMessageId) {
          const messageStore = useMessageStore.getState();
          const message = messageStore.getMessageById(streamingMessageId);
          
          // Tool execution started, complete any active thinking
          if (message && message.currentThinkingSubject) {
            logger.debug('🔧 Completing thinking before tool execution');
            messageStore.completeCurrentThinking(streamingMessageId);
          }
        }
        
        // Simplified tool tracking - just log the tool call
        logger.debug('🔧 Tool executing in background:', parsedData.toolName);
        
      } catch (error) {
        console.error('❌ Error handling global tool_call_request:', error);
      }
    };

    this.eventHandlers.handleToolResponse = (event: CustomEvent) => {
      try {
        const { data } = event.detail;
        const parsedData: ToolCallResponseEvent = JSON.parse(data);
        
        // Simplified tool response handling - just log completion
        logger.debug('✅ Tool completed in background:', parsedData);
        
      } catch (error) {
        console.error('❌ Error handling global tool_call_response:', error);
      }
    };

    // Other event handlers
    this.eventHandlers.handleToolConfirmation = (event: CustomEvent) => {
      try {
        const { data } = event.detail;
        const parsedData: ToolConfirmationRequiredEvent = JSON.parse(data);
        useMessageStore.getState().addPendingConfirmation(parsedData.messageId, parsedData.toolCallId, parsedData.confirmationDetails);
        
      } catch (error) {
        console.error('❌ Error handling global tool_confirmation_required:', error);
      }
    };

    this.eventHandlers.handleConversationStatus = (event: CustomEvent) => {
      try {
        const { data } = event.detail;
        const parsedData: ConversationStatusEvent = JSON.parse(data);
        useMessageStore.getState().setConversationStatus(parsedData.status, parsedData.reason);
        
      } catch (error) {
        console.error('❌ Error handling global conversation_status:', error);
      }
    };

    this.eventHandlers.handleThinking = (event: CustomEvent) => {
      try {
        const { data } = event.detail;
        const parsedData = JSON.parse(data);

        const streamingMessageId = useMessageStore.getState().streamingMessageId;
        if (streamingMessageId) {
          // Log thinking events for debugging with timing
          const now = Date.now();
          console.log('💭 Thinking event received:', {
            messageId: streamingMessageId,
            subject: parsedData.subject,
            thinking: parsedData.thinking?.substring(0, 100) + '...',
            timestamp: parsedData.timestamp,
            receivedAt: now,
            timeSinceLastThinking: (window as any).__lastThinkingTime ? now - (window as any).__lastThinkingTime : 0
          });
          (window as any).__lastThinkingTime = now;

          // Check if this is the first real thinking event
          const currentMessage = useMessageStore.getState().getMessageById(streamingMessageId);

          if (currentMessage) {
            // Check if we have placeholder subjects (they were added within the last 5 seconds)
            const now = Date.now();
            const placeholderTimeWindow = 5000; // 5 seconds

            const hasPlaceholders = currentMessage.thinkingHistory?.some(t =>
              (now - t.timestamp) < placeholderTimeWindow && !t.completed
            );

            if (hasPlaceholders) {
              // Complete all recent placeholder subjects when real thinking starts
              const updatedHistory = currentMessage.thinkingHistory?.map(entry => {
                const isRecent = (now - entry.timestamp) < placeholderTimeWindow;
                return isRecent && !entry.completed
                  ? { ...entry, completed: true }
                  : entry;
              }) || [];

              useMessageStore.getState().updateMessage(streamingMessageId, {
                thinkingHistory: updatedHistory
              });

              console.log('✅ Marked placeholder thinking as completed, real thinking started');
            }
          }

          // Update both thinking content and subject for hybrid approach
          // This allows the ThinkingIndicator to extract chapters from content
          useMessageStore.getState().updateMessage(streamingMessageId, {
            currentThinking: parsedData.thinking,
            currentThinkingSubject: parsedData.subject
          });

          // Also add to thinking history for persistence
          useMessageStore.getState().addThinkingSubject(streamingMessageId, parsedData.subject);

        } else {
          console.warn('⚠️ Thinking event received but no streaming message ID set yet');
        }

      } catch (error) {
        console.error('❌ Error handling global thinking event:', error);
      }
    };

    this.eventHandlers.handleAgentActivity = (event: CustomEvent) => {
      try {
        const { data } = event.detail;
        const parsedData = JSON.parse(data);

        const streamingMessageId = useMessageStore.getState().streamingMessageId;
        if (streamingMessageId) {
          // Only process THOUGHT_CHUNK events - these contain agent thinking subjects
          if (parsedData.activity?.type === 'THOUGHT_CHUNK') {
            const subject = parsedData.activity.content;
            const agentId = parsedData.activity.agentId;

            console.log('🤖 Agent activity (thought) received:', {
              messageId: streamingMessageId,
              agentId: agentId,
              subject: subject?.substring(0, 100)
            });

            // Update current thinking subject (displayed in ThinkingIndicator)
            useMessageStore.getState().updateMessage(streamingMessageId, {
              currentThinkingSubject: subject
            });

            // Add to thinking history for persistence
            useMessageStore.getState().addThinkingSubject(streamingMessageId, subject);
          }
        } else {
          console.warn('⚠️ Agent activity event received but no streaming message ID set yet');
        }

      } catch (error) {
        console.error('❌ Error handling agent activity event:', error);
      }
    };

    this.eventHandlers.handleChatCompressed = (event: CustomEvent) => {
      try {
        const { conversationId, data } = event.detail;
        const parsedData = JSON.parse(data);
        
        useMessageStore.getState().setCompressionStatus(conversationId, false);
        
        if (parsedData.compressionInfo) {
          useMessageStore.getState().addCompressionMessage(conversationId, parsedData.compressionInfo);
        }
        
      } catch (error) {
        console.error('❌ Error handling global chat_compressed:', error);
      }
    };

    this.eventHandlers.handleStreamError = (event: CustomEvent) => {
      try {
        const { messageId, data } = event.detail;
        
        
        if (messageId) {
          const messageStore = useMessageStore.getState();
          let errorMessage = 'Stream error occurred';
          
          try {
            if (data) {
              const parsedError = JSON.parse(data);
              errorMessage = parsedError.message || parsedError.error?.message || errorMessage;
            }
          } catch (e) {
            // Use default error message if parsing fails
          }
          
          // Update message with error status
          messageStore.updateMessage(messageId, {
            status: 'error',
            metadata: {
              streamComplete: true,
              error: errorMessage,
            },
          });

          // Clear streaming state and thinking
          messageStore.setStreamingMessage(null);
          messageStore.clearToolProgress(messageId);
          messageStore.clearAllThinking(messageId);
          
          // Always set to completed on error - no hanging statuses
          messageStore.setConversationStatus(ConversationStatus.Completed);
          
          // Error occurred, clear all thinking
          const currentMessage = messageStore.getMessageById(messageId);
          if (currentMessage) {
            logger.debug('🔧 Clearing thinking on error');
          }
          
          logger.error('❌ Stream error handled for message:', messageId, errorMessage);
        }
        
      } catch (error) {
        console.error('❌ Error handling stream error event:', error);
      }
    };

    // Retry status handler
    this.eventHandlers.handleRetryStatus = (event: CustomEvent) => {
      try {
        const { data } = event.detail;
        const parsedData = JSON.parse(data);
        
        const messageStore = useMessageStore.getState();
        const message = messageStore.getMessageById(parsedData.messageId);
        
        if (message) {
          messageStore.updateMessage(parsedData.messageId, {
            metadata: {
              ...message.metadata,
              retryStatus: {
                isRetrying: true,
                attempt: parsedData.attempt,
                maxAttempts: parsedData.maxAttempts,
                nextRetryIn: parsedData.nextRetryIn,
                error: parsedData.error,
                delayMs: parsedData.delayMs
              }
            }
          });
        }
        
        logger.info(`🔄 Retry status: attempt ${parsedData.attempt}/${parsedData.maxAttempts} for message ${parsedData.messageId}`);
      } catch (error) {
        console.error('❌ Error handling retry status event:', error);
      }
    };

    // Retry countdown handler
    this.eventHandlers.handleRetryCountdown = (event: CustomEvent) => {
      try {
        const { data } = event.detail;
        const parsedData = JSON.parse(data);

        const messageStore = useMessageStore.getState();
        const message = messageStore.getMessageById(parsedData.messageId);

        if (message && message.metadata?.retryStatus) {
          messageStore.updateMessage(parsedData.messageId, {
            metadata: {
              ...message.metadata,
              retryStatus: {
                ...message.metadata.retryStatus,
                remainingSeconds: parsedData.remainingSeconds,
                totalSeconds: parsedData.totalSeconds
              }
            }
          });
        }

        logger.debug(`⏱️ Retry countdown: ${parsedData.remainingSeconds}s remaining for message ${parsedData.messageId}`);
      } catch (error) {
        console.error('❌ Error handling retry countdown event:', error);
      }
    };

    // Tool status update handler (keep-alive during long tool executions)
    this.eventHandlers.handleToolStatus = (event: CustomEvent) => {
      try {
        const { data } = event.detail;
        const parsedData = JSON.parse(data);

        logger.debug(`🔧 Tool status: ${parsedData.toolName} - ${parsedData.status}`);

        // This event serves primarily as a keep-alive signal
        // No UI updates needed, but could be used for debugging or progress tracking

      } catch (error) {
        console.error('❌ Error handling tool status event:', error);
      }
    };

    // Set up global event listeners
    const eventTypes = [
      'stream-message_start',
      'stream-message_chunk',
      'stream-message_complete',
      'stream-token_count_update',
      'stream-tool_call_request',
      'stream-tool_call_response',
      'stream-tool_confirmation_required',
      'stream-conversation_status',
      'stream-thinking',
      'stream-agent_activity',
      'stream-chat_compressed',
      'stream-error',
      'stream-retry_status',
      'stream-retry_countdown',
      'stream-tool_status'
    ];

    const handlers = [
      this.eventHandlers.handleMessageStart,
      this.eventHandlers.handleMessageChunk,
      this.eventHandlers.handleMessageComplete,
      this.eventHandlers.handleTokenCountUpdate,
      this.eventHandlers.handleToolRequest,
      this.eventHandlers.handleToolResponse,
      this.eventHandlers.handleToolConfirmation,
      this.eventHandlers.handleConversationStatus,
      this.eventHandlers.handleThinking,
      this.eventHandlers.handleAgentActivity,
      this.eventHandlers.handleChatCompressed,
      this.eventHandlers.handleStreamError,
      this.eventHandlers.handleRetryStatus,
      this.eventHandlers.handleRetryCountdown,
      this.eventHandlers.handleToolStatus
    ];

    // Clean up any existing listeners and add new ones
    eventTypes.forEach((eventType, index) => {
      window.removeEventListener(eventType, handlers[index] as EventListener);
      window.addEventListener(eventType, handlers[index] as EventListener);
    });

    this.eventListenersSetup = true;
    logger.debug('✅ Global stream event listeners set up');
  }

  /**
   * Send a message to a conversation via HTTP POST
   */
  async sendMessage(data: SendMessageData): Promise<string> {
    logger.debug('📤 Sending message via HTTP:', data);

    // Check if this is likely a first message (no existing messages for this conversation)
    const existingMessages = useMessageStore.getState().getMessagesForConversation(data.conversationId);
    const isFirstMessage = existingMessages.length === 0;
    
    if (isFirstMessage) {
      console.log('🎯 FIRST MESSAGE: Detected first message for conversation:', data.conversationId);
      console.log('🎯 FIRST MESSAGE: Current message store state for conversation:', existingMessages);
    }

    try {
      let userMessageId: string;
      
      // Only create user message if not skipped (for hero page flow, it's already created)
      if (!data.skipUserMessage) {
        // Create optimistic user message
        userMessageId = uuidv4();
        
        // Check if user message already exists to prevent duplicates
        const existingUserMessage = useMessageStore.getState().getMessageById(userMessageId);
        if (existingUserMessage) {
          logger.warn('⚠️ DUPLICATE: User message already exists, skipping creation:', userMessageId);
          return userMessageId;
        }

        const userMessage = {
          id: userMessageId,
          conversationId: data.conversationId,
          role: 'user' as const,
          content: data.content,
          timestamp: new Date(),
          attachments: [],
          metadata: {
            streamComplete: true,
          },
          status: 'sent' as const,
          thinkingHistory: [],
          currentThinkingSubject: undefined,
          currentThinking: undefined,
        };

        // Add user message immediately for optimistic UI
        logger.debug('🆕 Creating new user message:', userMessageId);
        useMessageStore.getState().addMessage(userMessage);
      } else {
        // User message was already created, generate a dummy ID for return value
        userMessageId = 'pre-created-' + Date.now();
        console.log('✅ Skipping user message creation (already created in hero flow)');
      }
      
      // If a placeholder was already created in the UI, use it; otherwise create one here
      // This handles backward compatibility for components that don't pre-create placeholders
      let placeholderAIMessageId = data.placeholderMessageId;
      
      // Verify the provided placeholder exists and is still a placeholder
      if (placeholderAIMessageId) {
        const existingPlaceholder = useMessageStore.getState().getMessageById(placeholderAIMessageId);
        console.log('🔍 Checking placeholder:', {
          id: placeholderAIMessageId,
          found: !!existingPlaceholder,
          isPlaceholder: existingPlaceholder?.metadata?.isPlaceholder,
          metadata: existingPlaceholder?.metadata
        });

        if (existingPlaceholder && existingPlaceholder.metadata?.isPlaceholder) {
          console.log('✅ Using pre-created placeholder AI message:', placeholderAIMessageId);
          // Set the streaming message to the pre-created placeholder
          useMessageStore.getState().setStreamingMessage(placeholderAIMessageId);
        } else {
          console.log('⚠️ Provided placeholder ID is invalid or not a placeholder, will create new one');
          console.log('   Placeholder details:', {
            exists: !!existingPlaceholder,
            hasMetadata: !!existingPlaceholder?.metadata,
            isPlaceholder: existingPlaceholder?.metadata?.isPlaceholder
          });
          placeholderAIMessageId = undefined;
        }
      }
      
      if (!placeholderAIMessageId) {
        // Fallback: Create placeholder here if not provided (for backward compatibility)
        placeholderAIMessageId = uuidv4();
        
        // Generate contextual placeholder subjects based on the user's message
        let placeholderThinkingSubjects = [
          'Analyzing your request',
          'Understanding context', 
          'Processing information'
        ];
        
        const msgLower = data.content.toLowerCase();
        
        // Detect type of request and customize placeholders
        if (msgLower.includes('slide') || msgLower.includes('presentation')) {
          placeholderThinkingSubjects = [
            'Analyzing slide requirements',
            'Reviewing presentation structure',
            'Preparing visual elements'
          ];
        } else if (msgLower.includes('image') || msgLower.includes('visual') || msgLower.includes('picture')) {
          placeholderThinkingSubjects = [
            'Understanding visual requirements',
            'Preparing image generation',
            'Analyzing design parameters'
          ];
        } else if (msgLower.includes('fix') || msgLower.includes('error') || msgLower.includes('bug')) {
          placeholderThinkingSubjects = [
            'Identifying the issue',
            'Analyzing error context',
            'Formulating solution'
          ];
        } else if (msgLower.includes('create') || msgLower.includes('build') || msgLower.includes('make')) {
          placeholderThinkingSubjects = [
            'Planning creation process',
            'Gathering requirements',
            'Structuring approach'
          ];
        } else if (msgLower.includes('explain') || msgLower.includes('what') || msgLower.includes('how')) {
          placeholderThinkingSubjects = [
            'Analyzing your question',
            'Gathering information',
            'Structuring explanation'
          ];
        }
        
        const placeholderAIMessage = {
          id: placeholderAIMessageId!, // Guaranteed to be defined after line 726
          conversationId: data.conversationId,
          role: 'assistant' as const,
          content: '',
          timestamp: new Date(),
          attachments: [],
          metadata: {
            streamComplete: false,
            isPlaceholder: true, // Mark as placeholder to be replaced
          },
          status: 'streaming' as const,
          thinkingHistory: [
            {
              subject: placeholderThinkingSubjects[0],
              timestamp: Date.now(),
              completed: false
            }
          ],
          currentThinkingSubject: placeholderThinkingSubjects[0],
          currentThinking: 'Initializing AI processing and analyzing your request...',
        };
        
        console.log('🎯 Creating fallback placeholder AI message for instant feedback');
        useMessageStore.getState().addMessage(placeholderAIMessage);
        useMessageStore.getState().setStreamingMessage(placeholderAIMessageId!);
        
        // Add more placeholders progressively to show activity
        placeholderThinkingSubjects.slice(1).forEach((subject, index) => {
          setTimeout(() => {
            const currentMsg = useMessageStore.getState().getMessageById(placeholderAIMessageId!);
            // Only add placeholder if still a placeholder and not replaced
            if (currentMsg && currentMsg.metadata?.isPlaceholder) {
              useMessageStore.getState().addThinkingSubject(placeholderAIMessageId!, subject);
            }
          }, (index + 1) * 600); // Stagger placeholders every 600ms
        });
        
        console.log('✅ Fallback placeholder AI message added with thinking subjects:', placeholderThinkingSubjects);
      }
      
      // Store the placeholder ID to clean it up when real message arrives
      (window as any).__placeholderMessageId = placeholderAIMessageId;

      // Prepare request payload
      // Use content as-is - no automatic video_initializer triggering
      // Workflow is now user-driven via "Create Plan" button
      const messageContent = data.content;

      const payload: any = {
        content: messageContent,
        attachments: data.attachments,
        model: data.model,
        provider: data.provider, // AI provider: 'gemini' | 'claude'
      };

      // Add images if present
      if (data.images && data.images.length > 0) {
        payload.images = data.images.map(img => ({
          data: img.data, // already base64 encoded
          mimeType: img.mimeType,
          filename: img.filename
        }));
      }

      // Send message to API using authenticated API instance
      const response = await api.post<SendMessageResponse>(
        `/conversations/${data.conversationId}/messages`,
        payload
      );

      logger.info('✅ Message sent, starting SSE stream:', response.data.streamUrl);

      // Update user message with metadata from server if provided
      if (response.data.message?.metadata && !data.skipUserMessage) {
        // Only update if we created the user message
        const existingMessage = useMessageStore.getState().getMessageById(userMessageId);
        if (existingMessage) {
          useMessageStore.getState().updateMessage(userMessageId, {
            metadata: {
              ...existingMessage.metadata,
              ...response.data.message.metadata
            }
          });
          logger.debug('📊 Updated user message with server metadata:', response.data.message.metadata);
        }
      }

      if (isFirstMessage) {
        console.log('🎯 FIRST MESSAGE: Starting SSE stream for first message');
      }

      // Start SSE stream for AI response
      this.startSSEStream(data.conversationId, response.data.streamUrl, isFirstMessage);

      return userMessageId;

    } catch (error) {
      console.error('❌ Failed to send message:', error);
      
      // Handle error by updating message store
      useMessageStore.getState().setError(
        error instanceof Error ? error.message : 'Failed to send message'
      );
      
      throw error;
    }
  }

  /**
   * Start Server-Sent Events stream for AI response using enhanced stream manager
   */
  startSSEStream(conversationId: string, streamUrl: string, isFirstMessage: boolean = false): void {
    logger.debug('📡 SSE DEBUG: Starting enhanced SSE stream:', streamUrl);
    
    if (isFirstMessage) {
      console.log('🎯 FIRST MESSAGE SSE: Starting stream for first message in conversation:', conversationId);
    }

    // Set up stream recovery event listeners for this specific conversation
    this.setupStreamRecoveryListeners(conversationId);

    // Use the enhanced stream manager (global event listeners are already set up)
    console.log('📡 Creating SSE stream:', { conversationId, streamUrl, isFirstMessage });
    streamManager.createStream(conversationId, streamUrl);
    console.log('✅ SSE stream creation initiated for:', conversationId);
  }

  /**
   * Set up listeners for stream recovery events
   */
  private setupStreamRecoveryListeners(_conversationId: string): void {
    if (!this.eventHandlers.handleStreamRecovery) {
      this.eventHandlers.handleStreamRecovery = (event: CustomEvent) => {
        const { messageId, messageStatus } = event.detail;
        
        logger.info('🔄 Stream recovery: Message completion detected via polling:', messageId);
        
        // Handle the completion as if it came from SSE
        this.handleRecoveredMessageComplete(messageId, messageStatus);
      };
    }

    // Clean up existing listeners
    window.removeEventListener('stream-recovery-complete', this.eventHandlers.handleStreamRecovery as EventListener);
    
    // Add new listener
    window.addEventListener('stream-recovery-complete', this.eventHandlers.handleStreamRecovery as EventListener);
  }


  /**
   * Handle message completion (both from SSE and recovery)
   */
  private handleMessageCompletion(data: any, conversationId: string): void {
    logger.info('✅ AI message streaming completed:', data);

    if (data.messageId) {
      const messageStore = useMessageStore.getState();
      const currentMessage = messageStore.getMessageById(data.messageId);
      
      // Update output token count if provided
      if (data.metadata?.outputTokenCount !== undefined) {
        messageStore.updateMessage(data.messageId, {
          metadata: {
            ...currentMessage?.metadata,
            outputTokenCount: data.metadata.outputTokenCount,
            streamComplete: true
          }
        });
      }
      
      // Complete any remaining thinking
      if (currentMessage && currentMessage.currentThinkingSubject) {
        messageStore.completeCurrentThinking(data.messageId);
      }
      
      messageStore.updateMessage(data.messageId, {
        content: data.finalContent || currentMessage?.content,
        metadata: {
          ...currentMessage?.metadata,
          ...data.metadata,
          streamComplete: true,
          retryStatus: undefined, // Clear retry status on successful completion
        },
        status: 'sent',
      });

      messageStore.setStreamingMessage(null);
      messageStore.clearToolProgress(data.messageId);
      messageStore.clearAllThinking(data.messageId);
      
      // Always set to completed when message finishes - no intermediate statuses
      messageStore.setConversationStatus(ConversationStatus.Completed);

      useConversationStore.getState().updateConversation(conversationId, {
        messageCount: messageStore.getMessagesForConversation(conversationId).length,
        lastActivity: new Date(),
      });
    }
  }

  /**
   * Handle recovered message completion
   */
  private handleRecoveredMessageComplete(messageId: string, _messageStatus: any): void {
    const message = useMessageStore.getState().getMessageById(messageId);
    
    if (message) {
      const messageStore = useMessageStore.getState();
      
      messageStore.updateMessage(messageId, {
        metadata: {
          ...message.metadata,
          streamComplete: true,
        },
        status: 'sent',
      });

      messageStore.setStreamingMessage(null);
      messageStore.clearToolProgress(messageId);
      messageStore.clearAllThinking(messageId);
      
      // Set to completed - simple and consistent
      messageStore.setConversationStatus(ConversationStatus.Completed);

      useConversationStore.getState().updateConversation(message.conversationId, {
        messageCount: messageStore.getMessagesForConversation(message.conversationId).length,
        lastActivity: new Date(),
      });
    }
  }

  // Tool response handling removed for simplicity - tools execute in background
  // This eliminates the blocking UI issue during tool execution

  /**
   * Close SSE stream for a conversation
   */
  closeStream(conversationId: string): void {
    logger.debug('🔌 Closing SSE stream for conversation:', conversationId);
    streamManager.closeStream(conversationId);
  }

  /**
   * Close all active streams
   */
  closeAllStreams(): void {
    if (import.meta.env.DEV) {
      console.warn('🚨 closeAllStreams() called - this may interrupt active streams!', new Error().stack);
    }
    logger.debug('🔌 Closing all SSE streams');
    streamManager.closeAllStreams();
  }

  /**
   * Get status of active streams
   */
  getActiveStreams(): string[] {
    return streamManager.getActiveStreams();
  }

  /**
   * Check if a conversation has an active stream
   */
  hasActiveStream(conversationId: string): boolean {
    return streamManager.hasActiveStream(conversationId);
  }

  /**
   * Send tool confirmation response
   */
  async sendToolConfirmation(
    conversationId: string,
    toolCallId: string,
    outcome: ToolConfirmationOutcome
  ): Promise<void> {
    try {
      logger.debug('📝 Sending tool confirmation:', { toolCallId, outcome });
      
      const response = await api.post(
        `/conversations/${conversationId}/tool-confirmations/${toolCallId}/respond`,
        { outcome }
      );
      
      logger.debug('✅ Tool confirmation sent successfully:', response.data);
      
      // Remove from pending confirmations
      useMessageStore.getState().removePendingConfirmation(toolCallId);
      
    } catch (error) {
      console.error('❌ Failed to send tool confirmation:', error);
      useMessageStore.getState().setError(
        error instanceof Error ? error.message : 'Failed to send confirmation'
      );
      throw error;
    }
  }

  /**
   * Interrupt an active message stream
   */
  async interruptMessage(conversationId: string): Promise<void> {
    try {
      logger.debug('🛑 Interrupting message for conversation:', conversationId);
      
      // Call the interrupt endpoint
      const response = await api.post(`/conversations/${conversationId}/messages/interrupt`);
      
      logger.debug('✅ Message interrupted successfully:', response.data);
      
      // Close any active SSE streams for this conversation
      this.closeStream(conversationId);
      
      // Update the message store to clear streaming state
      useMessageStore.getState().setStreamingMessage(null);
      useMessageStore.getState().setError(null);
      
      return response.data;
      
    } catch (error) {
      console.error('❌ Failed to interrupt message:', error);
      useMessageStore.getState().setError(
        error instanceof Error ? error.message : 'Failed to interrupt message'
      );
      throw error;
    }
  }
}

// Create singleton instance
export const messageService = new MessageService();
export default messageService;